import { Component, Suspense, lazy, memo, type ReactNode } from 'react';
import { Button } from 'antd';
import {
  useWorkReplay,
  type UseWorkReplayOptions,
} from '../../../work-replay/useWorkReplay';
import {
  CANVAS_LABELS,
  LANGUAGE_LABELS,
  type ReplayManifest,
  type WorkReplayView,
} from '../../../work-replay/types';
import { length, longDate, taskName } from './format';
import styles from './index.module.css';

/** The player loads only when someone chooses to watch. */
const Player = lazy(() => import('./Player'));

/**
 * If the player's own code can't load (a dropped connection, a deploy that
 * replaced it) or fails while drawing, the section says so and offers to try
 * again, instead of taking the candidate page down with it.
 */
class PlayerBoundary extends Component<
  { onRetry: () => void; children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className={styles.note} role="status">
        <p className={styles.noteText}>The player couldn&apos;t be opened.</p>
        <Button
          type="link"
          size="small"
          onClick={() => {
            this.setState({ failed: false });
            this.props.onRetry();
          }}
        >
          Try again
        </Button>
      </div>
    );
  }
}

export interface WorkReplaySectionProps extends Pick<
  UseWorkReplayOptions,
  'fetchView' | 'load' | 'now'
> {
  jobId: number;
  candidateId: number;
  /** The anonymised view never asks: the backend refuses it, and the page says why. */
  masked?: boolean;
  /** Opens the interview transcript, so the two sit side by side. */
  onOpenTranscript?: () => void;
}

const languageLabel = (language: string) =>
  LANGUAGE_LABELS[language as keyof typeof LANGUAGE_LABELS] ??
  CANVAS_LABELS[language as keyof typeof CANVAS_LABELS] ??
  language;

const isCanvasTask = (language: string) => language in CANVAS_LABELS;

/** The facts a recruiter can read before pressing play, all from the signed manifest. */
const factsOf = (manifest: ReplayManifest): string[] => {
  const facts: string[] = [];
  const { final } = manifest;
  if (isCanvasTask(manifest.language)) facts.push('Drawing submitted');
  else if (final.ran)
    facts.push(
      `${final.passed} passed${final.failed ? ` · ${final.failed} failed` : ''} when submitted`,
    );
  else facts.push('Submitted without running the tests');
  if (manifest.chunks.some((c) => c.kind === 'audio'))
    facts.push('Voice walkthrough');
  const frames = manifest.chunks.filter((c) => c.kind === 'screen').length;
  if (frames)
    facts.push(
      `Screen captured while away (${frames} frame${frames === 1 ? '' : 's'})`,
    );
  return facts;
};

/** The poster's tile: code brackets for a code task, a small diagram for a canvas. */
const TaskGlyph = ({ canvas }: { canvas: boolean }) =>
  canvas ? (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <rect
        x="3"
        y="4"
        width="7"
        height="6"
        rx="1.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <rect
        x="14"
        y="14"
        width="7"
        height="6"
        rx="1.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M10 7h3.5a1.5 1.5 0 0 1 1.5 1.5V14"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true">
      <path
        d="m8.5 7-5 5 5 5M15.5 7l5 5-5 5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );

const Note = ({
  children,
  action,
}: {
  children: React.ReactNode;
  action?: React.ReactNode;
}) => (
  <div className={styles.note} role="status">
    <p className={styles.noteText}>{children}</p>
    {action}
  </div>
);

/** Every replay this job holds for the candidate, when there is more than one (phase B shares). */
const OtherReplays = ({
  view,
  onChoose,
}: {
  view: WorkReplayView;
  onChoose: (assetId: string) => void;
}) => {
  if (view.granted.length < 2) return null;
  return (
    <div className={styles.others}>
      <p className={styles.othersLabel}>Replays for this job</p>
      <ul className={styles.othersList}>
        {view.granted.map((g) => (
          <li key={g.assetId}>
            <button
              type="button"
              className={styles.other}
              aria-pressed={g.assetId === view.assetId}
              onClick={() => onChoose(g.assetId)}
            >
              {taskName(g.taskId)}
              <span className={styles.otherMeta}>
                {[languageLabel(g.language), longDate(g.completedAt)]
                  .filter(Boolean)
                  .join(' · ')}
                {g.grantType === 'share' ? ' · shared by the candidate' : ''}
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

/**
 * The candidate's work replay on the candidate page: the work space from
 * Rio's interview, replayed as they worked, beside the transcript. Shown when
 * REACT_APP_WORK_REPLAY is on; the page decides that.
 *
 * Asks avrio-backend once when it opens (for the summary, or the empty
 * state), and fetches the recording itself only when the recruiter chooses to
 * watch. "Verified by Avrio" appears only when every part matched Avrio's
 * signed record; otherwise the player says, neutrally, that it couldn't
 * verify.
 */
export const WorkReplaySection = memo(
  ({
    jobId,
    candidateId,
    masked = false,
    onOpenTranscript,
    fetchView,
    load,
    now,
  }: WorkReplaySectionProps) => {
    const { summary, playback, watch, choose, retry } = useWorkReplay({
      jobId,
      candidateId,
      masked,
      fetchView,
      load,
      now,
    });

    switch (summary.status) {
      case 'loading':
        return (
          <div className={styles.placeholder} role="status" aria-live="polite">
            <span className={styles.visuallyHidden}>
              Looking for a work replay
            </span>
          </div>
        );
      case 'masked':
        return (
          <Note>
            The work replay isn&apos;t available in the anonymised view: it can
            show the candidate&apos;s screen and voice.
          </Note>
        );
      case 'none':
        return (
          <Note>
            No work replay for this interview. Rio records one when the
            interview includes a work task.
          </Note>
        );
      case 'saving':
        return (
          <Note
            action={
              <Button type="link" size="small" onClick={retry}>
                Check again
              </Button>
            }
          >
            The work replay is still being saved.
          </Note>
        );
      case 'unavailable':
        return (
          <Note
            action={
              <Button type="link" size="small" onClick={retry}>
                Try again
              </Button>
            }
          >
            The work replay couldn&apos;t be loaded right now.
          </Note>
        );
      default:
        break;
    }

    const { view } = summary;
    const { manifest } = view;
    const name = taskName(manifest.taskId);

    if (playback.status === 'ready') {
      return (
        <div className={styles.section}>
          <OtherReplays view={view} onChoose={choose} />
          <PlayerBoundary onRetry={retry}>
            <Suspense
              fallback={
                <div className={styles.placeholder} aria-hidden="true" />
              }
            >
              <Player
                key={view.assetId}
                loaded={playback.loaded}
                taskName={name}
                startAt={playback.startAt}
                onOpenTranscript={onOpenTranscript}
              />
            </Suspense>
          </PlayerBoundary>
        </div>
      );
    }

    const loading = playback.status === 'loading';
    const meta = [
      languageLabel(manifest.language),
      length(manifest.durationMs),
      longDate(manifest.completedAt ?? manifest.createdAt),
    ].filter(Boolean);

    return (
      <div className={styles.section}>
        <OtherReplays view={view} onChoose={choose} />
        <div className={styles.poster} aria-busy={loading || undefined}>
          <span className={styles.posterTile}>
            <TaskGlyph canvas={isCanvasTask(manifest.language)} />
          </span>
          <div className={styles.posterText}>
            <p className={styles.posterTitle}>{name}</p>
            <p className={styles.posterMeta}>{meta.join(' · ')}</p>
            <ul className={styles.facts}>
              {factsOf(manifest).map((fact) => (
                <li key={fact}>{fact}</li>
              ))}
            </ul>
            {loading ? (
              <div className={styles.progress} role="status" aria-live="polite">
                <span className={styles.progressTrack} aria-hidden="true">
                  <span
                    className={styles.progressFill}
                    style={{
                      width: `${
                        playback.total
                          ? Math.round((playback.done / playback.total) * 100)
                          : 4
                      }%`,
                    }}
                  />
                </span>
                <span className={styles.progressText}>
                  {playback.total
                    ? `Loading the replay: ${playback.done} of ${playback.total} parts`
                    : 'Loading the replay'}
                </span>
              </div>
            ) : (
              <div className={styles.posterActions}>
                <Button
                  type="primary"
                  onClick={() =>
                    void watch('start', playback.status === 'failed')
                  }
                >
                  Watch the replay
                </Button>
                <Button
                  onClick={() =>
                    void watch('end', playback.status === 'failed')
                  }
                >
                  See the final state
                </Button>
              </div>
            )}
            {playback.status === 'failed' && (
              <p className={styles.failed} role="status">
                The replay couldn&apos;t be loaded. Its links may have expired;
                trying again asks for new ones.
              </p>
            )}
          </div>
        </div>
      </div>
    );
  },
);

WorkReplaySection.displayName = 'WorkReplaySection';

export default WorkReplaySection;
